/**
 * Syncs Yotpo rewards widget data (balance, available spend, tier) into display elements.
 * Use on any page: add data attributes to a parent and call initYotpoRewardsSync(parentElement).
 *
 * Required inside parent:
 *   [data-yotpo-rewards-host]     - element containing the .yotpo-widget-instance (hidden widget).
 *       Add data-yotpo-currency from cart.currency.iso_code (see Liquid on each host) for Intl formatting.
 *   [data-yotpo-rewards-balance]   - optional, where to show points count
 *   [data-yotpo-rewards-available] - optional, where to show monetary value in the shop currency
 *   [data-yotpo-rewards-tier]      - optional, where to show tier text
 *   [data-yotpo-rewards-history-trigger] - optional, element that when clicked will trigger the
 *       "rewards history" button inside the Yotpo widget (if found). Falls back to normal link behaviour if not found.
 */
(function () {
  /** Raw money value from Red Dots (100 dots = 5 in the shop currency major unit). */
  function dotsToMoney(points) {
    const num = Number(points);
    if (Number.isNaN(num) || num < 0) return null;
    return (num / 100) * 5;
  }

  /**
   * Vouchers are in 5 / 10 / 20 major units (combinable). Floor so we never show odd fractional vouchers.
   */
  function moneyToVoucherDisplay(raw) {
    const num = Number(raw);
    if (Number.isNaN(num) || num < 0) return null;
    return Math.floor(num / 5) * 5;
  }

  function getRewardsCurrencyCode(widgetHost) {
    if (!widgetHost || !widgetHost.getAttribute) return 'GBP';
    const code = widgetHost.getAttribute('data-yotpo-currency');
    return code && code.trim() ? code.trim() : 'GBP';
  }

  function formatCurrency(amount, currencyCode) {
    const code = currencyCode || 'GBP';
    const locale = document.documentElement.lang || undefined;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      return code + ' ' + Number(amount).toFixed(2);
    }
  }

  /**
   * Parse a numeric amount from widget text (supports £, $, €, CHF-style labels, and 1.234,56 / 1,234.56).
   */
  function parseMonetaryAmountFromText(text) {
    if (!text) return null;
    const trimmed = text.replace(/\u00a0/g, ' ').trim();
    const loose = trimmed.match(
      /(\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})|\d+[.,]\d{2}|\d+)/
    );
    if (!loose) return null;
    let raw = loose[1];
    if (raw.includes(',') && raw.includes('.')) {
      raw =
        raw.lastIndexOf(',') > raw.lastIndexOf('.')
          ? raw.replace(/\./g, '').replace(',', '.')
          : raw.replace(/,/g, '');
    } else if (raw.includes(',')) {
      const parts = raw.split(',');
      if (parts.length === 2 && parts[1].length <= 2) {
        raw = parts[0].replace(/\./g, '') + '.' + parts[1];
      } else {
        raw = raw.replace(/,/g, '');
      }
    }
    const num = parseFloat(raw);
    return Number.isNaN(num) ? null : num;
  }

  function parseMonetaryFromWidget(node) {
    if (!node || !node.textContent) return null;
    return parseMonetaryAmountFromText(node.textContent);
  }

  function parsePointsFromText(text) {
    if (!text) return null;
    const digits = text.replace(/[^\d]/g, '');
    if (!digits) return null;
    const num = parseInt(digits, 10);
    return Number.isNaN(num) ? null : num;
  }

  function formatInteger(value) {
    const locale = document.documentElement.lang || undefined;
    try {
      return new Intl.NumberFormat(locale).format(value);
    } catch (e) {
      return String(value);
    }
  }

  function syncFromYotpo(parent, widgetHost, balanceEl, availableEl, tierEl) {
    const currencyCode = getRewardsCurrencyCode(widgetHost);
    const balanceNode =
      widgetHost.querySelector('.yotpo-description-points-balance .number') ||
      widgetHost.querySelector('.yotpo-description-points-balance .yotpo-inner-text');
    const tierNode = widgetHost.querySelector('.yotpo-description-tier-status .yotpo-inner-text');
    let pointsValue = null;

    if (balanceNode) {
      pointsValue = parsePointsFromText(balanceNode.textContent);
      if (balanceEl && pointsValue !== null) {
        balanceEl.textContent = formatInteger(pointsValue);
      }
    }
    if (availableEl) {
      const availableNode =
        widgetHost.querySelector('.yotpo-description-available .number') ||
        widgetHost.querySelector('.yotpo-description-available .yotpo-inner-text') ||
        Array.from(widgetHost.querySelectorAll('.yotpo-description .yotpo-customized-text')).find(
          function (n) {
            return (
              /[$€£¥]|USD|EUR|GBP|available|spend/i.test(n.textContent) &&
              /\d+[.,]\d{1,2}/.test(n.textContent)
            );
          }
        );
      const monetaryFromWidget = parseMonetaryFromWidget(availableNode);
      if (monetaryFromWidget !== null) {
        const voucher = moneyToVoucherDisplay(monetaryFromWidget);
        if (voucher !== null) {
          availableEl.textContent = formatCurrency(voucher, currencyCode);
        }
      } else {
        const raw =
          pointsValue !== null
            ? dotsToMoney(pointsValue)
            : dotsToMoney(balanceEl && balanceEl.textContent.trim());

        const voucher = raw !== null ? moneyToVoucherDisplay(raw) : null;
        if (voucher !== null) {
          availableEl.textContent = formatCurrency(voucher, currencyCode);
        }
      }
    }
    if (tierNode && tierEl) {
      const value = tierNode.textContent.trim();
      if (value) tierEl.textContent = value;
    }
  }

  function startObserving(parent, widgetHost, balanceEl, availableEl, tierEl) {
    syncFromYotpo(parent, widgetHost, balanceEl, availableEl, tierEl);
    const observer = new MutationObserver(function () {
      syncFromYotpo(parent, widgetHost, balanceEl, availableEl, tierEl);
    });
    observer.observe(widgetHost, { childList: true, subtree: true });
  }

  function init(parent) {
    if (!parent || !parent.querySelector) return;
    if (parent.getAttribute('data-yotpo-rewards-inited') === 'true') return;
    const widgetHost = parent.querySelector('[data-yotpo-rewards-host]');
    const balanceEl = parent.querySelector('[data-yotpo-rewards-balance]');
    const availableEl = parent.querySelector('[data-yotpo-rewards-available]');
    const tierEl = parent.querySelector('[data-yotpo-rewards-tier]');
    const hasDisplayElements = balanceEl || availableEl || tierEl;
    const hasHistoryTrigger = parent.querySelector('[data-yotpo-rewards-history-trigger]');
    if (!widgetHost || (!hasDisplayElements && !hasHistoryTrigger)) return;
    parent.setAttribute('data-yotpo-rewards-inited', 'true');
    if (hasDisplayElements) {
      startObserving(parent, widgetHost, balanceEl, availableEl, tierEl);
    }
  }

  window.initYotpoRewardsSync = init;

  function autoInit() {
    var hosts = document.querySelectorAll('[data-yotpo-rewards-host]');
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      var parent = host;
      while (parent) {
        if (
          parent.querySelector('[data-yotpo-rewards-balance]') ||
          parent.querySelector('[data-yotpo-rewards-available]') ||
          parent.querySelector('[data-yotpo-rewards-tier]')
        ) {
          init(parent);
          break;
        }
        parent = parent.parentElement;
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
})();
